import React, { useState, useMemo, useEffect } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import { format, subMonths, isAfter } from 'date-fns';
import {
  Activity, AlertCircle, RefreshCw, ArrowRight, Info, TrendingUp,
  Bitcoin, Coins, Banknote, ChevronDown,
} from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

/**
 * LiquidityBridgeMonitor
 * ----------------------
 * Single React surface that fuses the "Repression Monitor" and "Gold → BTC
 * Lead-Lag" panels into one analyst view, driven entirely by the live
 * useMarketData hook. ZERO hardcoded prices, rates, or values.
 *
 * Renders:
 *   1. Live status banner (real-time spread + last-fetched timestamp)
 *   2. Stale-data warning if any source went stale
 *   3. Repression chart (CPI / FFR / BTC + per-segment red/green fill)
 *   4. Lead-lag interactive slider with live correlation display
 *   5. Skeleton on first load — never shows mock data as live
 */
export default function LiquidityBridgeMonitor({ proxyBase = '' }) {
  const market = useMarketData({ proxyBase });
  const { btc, gold, fred, derived, isLoading, lastUpdated, refetch } = market;

  // Slider state for lead-lag exploration (default: 6 months — historical norm)
  const [lagMonths, setLagMonths] = useState(6);
  const [lookbackMonths, setLookbackMonths] = useState(36);

  // Show full skeleton until ALL three sources have at least once succeeded.
  // No partial mock-data renders.
  const noDataYet = !btc.spot || !gold.spot || !fred.cpiYoY.length;
  const hasStaleSource = btc.status === 'stale' || gold.status === 'stale' || fred.status === 'stale';

  if (noDataYet && isLoading) {
    return <Skeleton message="Fetching live BTC, gold, and FRED data…" />;
  }
  if (noDataYet) {
    return <ErrorState market={market} onRetry={refetchAll(refetch)} />;
  }

  return (
    <div className="space-y-6">
      <LiveBanner market={market} />
      {hasStaleSource && <StaleWarning market={market} onRetry={refetchAll(refetch)} />}

      <RepressionChart
        cpiSeries={fred.cpiYoY}
        ffrSeries={fred.ffr}
        btcSeries={btc.history}
        lookbackMonths={lookbackMonths}
        setLookbackMonths={setLookbackMonths}
        derived={derived}
      />

      <LeadLagPanel
        goldHistory={gold.history}
        btcHistory={btc.history}
        leadLagCorrelation={derived.leadLagCorrelation}
        bestLag={derived.bestLag}
        lagMonths={lagMonths}
        setLagMonths={setLagMonths}
        gold={gold}
        btc={btc}
        lastUpdated={lastUpdated}
      />
    </div>
  );
}

const refetchAll = (refetch) => () => {
  refetch.btc();
  refetch.gold();
  refetch.fred();
};

// ────────────────────────────────────────────────────────────────────────────
// Live status banner
// ────────────────────────────────────────────────────────────────────────────
function LiveBanner({ market }) {
  const { derived, lastUpdated } = market;
  if (!derived?.spreadHeadline) return null;

  const realYield = derived.realYield;
  const tone =
    realYield < -0.25 ? { bg: 'bg-red-500/10',     border: 'border-red-500/40',     text: 'text-red-300' }
    : realYield > 0.25 ? { bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-300' }
    :                    { bg: 'bg-amber-500/10',  border: 'border-amber-500/40',  text: 'text-amber-300' };

  return (
    <div className={`flex items-start gap-4 p-4 rounded-xl border ${tone.bg} ${tone.border}`}>
      <Activity className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tone.text}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] font-bold tracking-widest uppercase ${tone.text}`}>Real-Time Spread</span>
          <LivePill status="live" />
        </div>
        <div className={`text-sm font-medium ${tone.text} font-mono tabular-nums break-all`}>
          {derived.spreadHeadline}
        </div>
      </div>
      <LastUpdated time={lastUpdated} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Repression chart — CPI vs FFR with per-segment red/green fill, BTC overlay
// ────────────────────────────────────────────────────────────────────────────
function RepressionChart({ cpiSeries, ffrSeries, btcSeries, lookbackMonths, setLookbackMonths, derived }) {
  // Align all three to a common monthly date range
  const chartData = useMemo(() => {
    const cutoff = subMonths(new Date(), lookbackMonths);
    const cpiMap = mapByMonth(cpiSeries);
    const ffrMap = mapByMonth(ffrSeries);
    const btcMap = mapByMonth(btcSeries);

    // Master keys: months where at least CPI and FFR exist
    const months = uniqueMonthsSince(cpiSeries, ffrSeries, cutoff);
    return months.map(d => {
      const k = monthKey(d);
      const cpi = cpiMap.get(k);
      const ffr = ffrMap.get(k);
      const btc = btcMap.get(k);
      const isRepression = cpi != null && ffr != null && cpi > ffr;
      return {
        date: format(d, 'MMM yy'),
        ts: d.getTime(),
        cpi, ffr, btc,
        repBase: isRepression ? Math.min(cpi, ffr) : null,
        repTop:  isRepression ? Math.max(cpi, ffr) : null,
        posBase: !isRepression && cpi != null && ffr != null ? Math.min(cpi, ffr) : null,
        posTop:  !isRepression && cpi != null && ffr != null ? Math.max(cpi, ffr) : null,
      };
    });
  }, [cpiSeries, ffrSeries, btcSeries, lookbackMonths]);

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-blue-500/[0.03] via-slate-900/60 to-red-500/[0.03] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-red-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-orange-400" />
              Financial Repression Monitor
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">CPI vs Federal Funds Rate — when CPI &gt; FFR, capital flees to hard assets</p>
          </div>
          <TimeframeButtons value={lookbackMonths} onChange={setLookbackMonths} />
        </div>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              />
              <YAxis
                yAxisId="rate"
                domain={[0, 'dataMax + 1']}
                tick={{ fill: 'rgba(74,158,255,0.9)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(74,158,255,0.2)' }}
                tickFormatter={(v) => `${v}%`}
              />
              <YAxis
                yAxisId="btc"
                orientation="right"
                tick={{ fill: 'rgba(247,147,26,0.9)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(247,147,26,0.2)' }}
                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
              />

              <Area yAxisId="rate" dataKey="repTop" baseLine={(p) => p.repBase}
                    stroke="none" fill="rgba(239,68,68,0.18)" isAnimationActive={false}
                    name="Repression Zone" connectNulls={false} />
              <Area yAxisId="rate" dataKey="posTop" baseLine={(p) => p.posBase}
                    stroke="none" fill="rgba(34,197,94,0.16)" isAnimationActive={false}
                    name="Positive Real Rates" connectNulls={false} />

              <Line yAxisId="rate" type="monotone" dataKey="cpi" stroke="#ef4444"
                    strokeWidth={2} dot={false} name="CPI YoY %" />
              <Line yAxisId="rate" type="monotone" dataKey="ffr" stroke="#4a9eff"
                    strokeWidth={2} dot={false} name="Fed Funds Rate %" />
              <Line yAxisId="btc" type="monotone" dataKey="btc" stroke="#f7931a"
                    strokeWidth={2} dot={false} name="BTC (USD)" />

              <Tooltip
                cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                content={<RepressionTooltip />}
              />
              <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} iconType="line"
                      formatter={(v) => <span className="text-slate-300">{v}</span>} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <MacroInsight derived={derived} />
      </div>
    </section>
  );
}

function MacroInsight({ derived }) {
  const [open, setOpen] = useState(false);
  if (derived?.cpi == null || derived?.ffr == null) return null;
  const { cpi, ffr, realYield } = derived;
  const headline = realYield < -0.25
    ? `🔥 Financial Repression Active: Cash is losing ${Math.abs(realYield).toFixed(2)}% in real terms`
    : realYield > 0.25
    ? `💼 Positive Real Rates: Cash is paying ${realYield.toFixed(2)}% above inflation`
    : `⏸️ Real Rates Near Zero: Neutral environment (real rate ${realYield >= 0 ? '+' : ''}${realYield.toFixed(2)}%)`;

  return (
    <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
              className="w-full flex items-start justify-between p-4 text-left hover:bg-white/[0.02] transition-colors">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold tracking-widest text-slate-400 mb-1">MACRO INSIGHT</div>
          <div className="text-sm font-semibold text-slate-100 leading-snug">{headline}</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 bg-slate-950/40 border border-white/10 rounded-lg overflow-hidden font-mono">
            <Stat label="CPI YoY" value={`${cpi.toFixed(2)}%`} dotColor="#ef4444" valueColor="text-red-400" />
            <Stat label="Fed Funds" value={`${ffr.toFixed(2)}%`} dotColor="#4a9eff" valueColor="text-blue-400" bordered />
            <Stat label="Real Yield"
                  value={`${realYield >= 0 ? '+' : ''}${realYield.toFixed(2)}%`}
                  valueColor={realYield < -0.25 ? 'text-red-400' : realYield > 0.25 ? 'text-emerald-400' : 'text-amber-400'}
                  sub="FFR − CPI" bordered />
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400 ml-4 flex-shrink-0">
          <span>{open ? 'Hide' : 'Details'}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-300 leading-relaxed space-y-3">
          <div className="bg-slate-900/60 rounded-md px-3 py-2 font-mono text-xs tabular-nums flex flex-wrap items-center gap-2">
            <span className="text-slate-500 font-bold">REAL YIELD =</span>
            <span className="text-blue-400">FFR ({ffr.toFixed(2)}%)</span>
            <span className="text-slate-500 font-bold">−</span>
            <span className="text-red-400">CPI ({cpi.toFixed(2)}%)</span>
            <span className="text-slate-500 font-bold">=</span>
            <span className={`font-bold ${realYield < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {realYield >= 0 ? '+' : ''}{realYield.toFixed(2)}%
            </span>
          </div>
          <p className="bg-slate-900/60 border-l-2 border-orange-500 pl-4 pr-3 py-2 rounded-r-md text-xs">
            <strong className="text-slate-100">The thesis:</strong> When CPI runs hotter than interest rates,
            savers are punished and capital flees into hard assets. Bitcoin's largest bull moves have
            historically coincided with these regimes.
          </p>
        </div>
      )}
    </div>
  );
}

function RepressionTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  if (!row) return null;
  const realYield = (row.ffr != null && row.cpi != null) ? row.ffr - row.cpi : null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur-md px-4 py-3 shadow-xl text-xs font-mono tabular-nums">
      <div className="text-slate-200 font-semibold mb-2">{row.date}</div>
      <div className="space-y-1">
        {row.cpi != null && <Row color="#ef4444" label="CPI" value={`${row.cpi.toFixed(2)}%`} />}
        {row.ffr != null && <Row color="#4a9eff" label="FFR" value={`${row.ffr.toFixed(2)}%`} />}
        {row.btc != null && <Row color="#f7931a" label="BTC" value={`$${Math.round(row.btc).toLocaleString()}`} />}
      </div>
      {realYield != null && (
        <div className="mt-2 pt-2 border-t border-white/10">
          Real yield:{' '}
          <span className={`font-semibold ${realYield >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {realYield >= 0 ? '+' : ''}{realYield.toFixed(2)}%
          </span>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Lead-Lag panel — slider + live correlation, normalized to selected window
// ────────────────────────────────────────────────────────────────────────────
function LeadLagPanel({
  goldHistory, btcHistory, leadLagCorrelation, bestLag,
  lagMonths, setLagMonths, gold, btc, lastUpdated,
}) {
  const goldSpot = gold?.spot;
  const btcSpot  = btc?.spot;
  const [windowMonths, setWindowMonths] = useState(48);

  // Normalise both series to start of the selected window (% gain since then)
  const series = useMemo(() => {
    if (!goldHistory.length || !btcHistory.length) return [];
    const goldWindow = goldHistory.slice(-windowMonths);
    const btcWindow  = btcHistory.slice(-windowMonths);
    const goldBase = goldWindow[0]?.value || 1;
    const btcBase  = btcWindow[0]?.value  || 1;
    return goldWindow.map((g, i) => {
      const btcShifted = btcWindow[i + lagMonths]; // BTC shifted FORWARD by lag
      return {
        date: format(g.date, 'MMM yy'),
        ts: g.date.getTime(),
        goldPct: ((g.value - goldBase) / goldBase) * 100,
        btcPct: btcShifted ? ((btcShifted.value - btcBase) / btcBase) * 100 : null,
      };
    });
  }, [goldHistory, btcHistory, windowMonths, lagMonths]);

  const liveCorr = leadLagCorrelation.find(c => c.lag === lagMonths);
  const bestCorr = bestLag;

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-amber-500/[0.03] via-slate-900/60 to-orange-500/[0.03] backdrop-blur-xl p-4 md:p-6">
      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-1">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              Gold → Bitcoin Lead Signal
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Compare normalised growth from the start of the window. BTC is shifted forward N months
              to test the lead-lag relationship.
            </p>
          </div>
          <TimeframeButtons value={windowMonths} onChange={setWindowMonths}
                            options={[
                              { label: '2Y',  months: 24 },
                              { label: '4Y',  months: 48 },
                              { label: '8Y',  months: 96 },
                              { label: 'Max', months: 200 },
                            ]} />
        </div>

        {/* Live spot prices — each tile shows its own freshness indicator
            so a silent fetch failure on one source doesn't masquerade as
            live data on the dashboard. */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <SpotTile
            icon={Coins} color="#f0c040" label="Gold spot"
            value={`$${goldSpot.toLocaleString(undefined, { maximumFractionDigits: 2 })}/oz`}
            status={gold?.status} fetchedAt={gold?.fetchedAt} error={gold?.error}
          />
          <SpotTile
            icon={Bitcoin} color="#f7931a" label="BTC spot"
            value={`$${Math.round(btcSpot).toLocaleString()}`}
            status={btc?.status} fetchedAt={btc?.fetchedAt} error={btc?.error}
          />
        </div>

        {/* Slider */}
        <div className="mt-5 p-4 bg-slate-900/40 border border-white/10 rounded-xl">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="text-sm font-semibold text-slate-100">Lag-Sync Slider</div>
              <div className="text-xs text-slate-500">Shift BTC forward in time to find the offset that matches gold</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Best historical fit</div>
              <div className="font-mono font-bold text-emerald-400 tabular-nums">
                {bestCorr ? `${bestCorr.lag}mo · score ${bestCorr.score}/100` : '—'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-500 font-mono">0mo</span>
            <input
              type="range" min="0" max="12" step="1" value={lagMonths}
              onChange={(e) => setLagMonths(parseInt(e.target.value))}
              className="flex-1 accent-amber-400"
            />
            <span className="text-[10px] text-slate-500 font-mono">12mo</span>
            <span className="font-mono text-amber-400 font-bold tabular-nums w-20 text-right">{lagMonths} months</span>
          </div>
          <div className="mt-2 text-xs text-slate-400">
            Live correlation at <strong>{lagMonths}-month lag</strong>:{' '}
            <span className={`font-mono font-bold ${
              (liveCorr?.score ?? 0) >= 70 ? 'text-emerald-400'
              : (liveCorr?.score ?? 0) >= 50 ? 'text-blue-400'
              : 'text-amber-400'
            }`}>
              {liveCorr ? `${liveCorr.score}/100 (r=${liveCorr.r.toFixed(2)})` : '—'}
            </span>
          </div>
        </div>

        {/* Chart */}
        <div className="h-64 mt-5">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false}
                     tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
              <Line type="monotone" dataKey="goldPct" stroke="#f0c040" strokeWidth={2.5} dot={false} name="Gold % gain" />
              <Line type="monotone" dataKey="btcPct"  stroke="#f7931a" strokeWidth={2} dot={false} strokeDasharray="4 3"
                    name={`BTC % gain (shifted +${lagMonths}mo)`} connectNulls={false} />
              <Tooltip
                cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                contentStyle={{ background: 'rgba(20,22,26,0.96)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}%` : '—'}
              />
              <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} iconType="line"
                      formatter={(v) => <span className="text-slate-300">{v}</span>} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Reusable UI atoms
// ────────────────────────────────────────────────────────────────────────────
function Skeleton({ message }) {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-20 rounded-xl bg-slate-900 border border-slate-800" />
      <div className="h-96 rounded-2xl bg-slate-900 border border-slate-800" />
      <div className="h-80 rounded-2xl bg-slate-900 border border-slate-800" />
      {message && (
        <div className="text-center text-xs text-slate-500 font-mono tracking-wide">
          {message}
        </div>
      )}
    </div>
  );
}

function ErrorState({ market, onRetry }) {
  const errors = [
    market.btc.error  && `BTC: ${market.btc.error}`,
    market.gold.error && `Gold: ${market.gold.error}`,
    market.fred.error && `FRED: ${market.fred.error}`,
  ].filter(Boolean);

  return (
    <div className="rounded-2xl border-2 border-red-500/40 bg-red-500/10 p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-red-300">Live data unavailable</h3>
          <p className="text-sm text-slate-300 mt-1">
            We couldn't reach one or more market data sources. The dashboard will not display
            outdated values — please retry once your connection is restored.
          </p>
          {errors.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs font-mono text-red-300/80">
              {errors.map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
          )}
          <button onClick={onRetry}
                  className="mt-4 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-colors">
            <RefreshCw className="w-4 h-4" /> Retry all sources
          </button>
        </div>
      </div>
    </div>
  );
}

function StaleWarning({ market, onRetry }) {
  const stale = [
    market.btc.status === 'stale'  && 'BTC',
    market.gold.status === 'stale' && 'Gold',
    market.fred.status === 'stale' && 'FRED',
  ].filter(Boolean);
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex items-center gap-3">
      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
      <div className="flex-1 text-sm text-amber-300 min-w-0">
        <strong>Stale data warning:</strong> {stale.join(', ')} {stale.length === 1 ? 'feed is' : 'feeds are'}{' '}
        unavailable. Showing values from{' '}
        <LastUpdated time={market.lastUpdated} inline />.
      </div>
      <button onClick={onRetry} className="text-xs px-3 py-1.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 transition-colors flex items-center gap-1.5">
        <RefreshCw className="w-3 h-3" /> Retry
      </button>
    </div>
  );
}

function LivePill({ status }) {
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest uppercase text-emerald-400">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      {status === 'live' ? 'LIVE' : status.toUpperCase()}
    </span>
  );
}

function LastUpdated({ time, inline }) {
  const [_, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force(n => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);
  if (!time) return inline ? <em>unknown</em> : null;
  const sec = Math.floor((Date.now() - time.getTime()) / 1000);
  const txt = sec < 60 ? `${sec}s ago`
    : sec < 3600 ? `${Math.floor(sec / 60)}m ago`
    : format(time, 'MMM d, p');
  if (inline) return <strong>{txt}</strong>;
  return (
    <div className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono flex-shrink-0">
      <div>Updated</div>
      <div className="text-slate-300">{txt}</div>
    </div>
  );
}

function TimeframeButtons({ value, onChange, options = [
  { label: '1Y', months: 12 },
  { label: '3Y', months: 36 },
  { label: '5Y', months: 60 },
  { label: '10Y', months: 120 },
] }) {
  return (
    <div className="inline-flex bg-slate-900/60 border border-white/10 rounded-lg p-0.5">
      {options.map(opt => (
        <button key={opt.label} onClick={() => onChange(opt.months)}
                className={`px-3 py-1 text-xs font-mono rounded-md transition-colors ${
                  value === opt.months
                    ? 'bg-orange-500 text-black font-bold'
                    : 'text-slate-300 hover:bg-white/5'
                }`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, sub, valueColor = 'text-slate-100', dotColor, bordered }) {
  return (
    <div className={`px-3 py-2 ${bordered ? 'sm:border-l border-white/10' : ''} border-b sm:border-b-0 border-white/10 last:border-0`}>
      <div className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest uppercase text-slate-500 mb-0.5">
        {dotColor && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />}
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-base font-bold tabular-nums ${valueColor}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SpotTile({ icon: Icon, color, label, value, status, fetchedAt, error }) {
  // Tick once per second so "X sec ago" stays fluid even though the
  // underlying spot price only refreshes every 60s.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const ageMs = fetchedAt ? Date.now() - new Date(fetchedAt).getTime() : null;
  // Treat anything over 3× the polling interval as "stale" visually, even
  // if the hook hasn't promoted it to 'stale' yet.
  const looksStale = (ageMs ?? 0) > 180_000 || status === 'stale' || status === 'error';
  const tone = status === 'error' || (looksStale && status !== 'live')
    ? { dot: 'bg-amber-400', text: 'text-amber-300', label: 'Stale' }
    : status === 'refreshing'
    ? { dot: 'bg-sky-400 animate-pulse', text: 'text-sky-300', label: 'Refreshing' }
    : { dot: 'bg-emerald-400 animate-pulse', text: 'text-emerald-300', label: 'Live' };

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}1f` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{label}</div>
          <div className={`flex items-center gap-1 text-[9px] font-bold tracking-wider uppercase ${tone.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
            <span>{tone.label}</span>
          </div>
        </div>
        <div className="text-lg font-bold tabular-nums" style={{ color }}>{value}</div>
        <div
          className="text-[10px] text-slate-500 font-mono mt-0.5 truncate"
          title={error || (fetchedAt ? `Last successful fetch: ${new Date(fetchedAt).toLocaleString()}` : '')}
        >
          {formatSpotAge(ageMs)}
        </div>
      </div>
    </div>
  );
}

function formatSpotAge(ms) {
  if (ms == null || !Number.isFinite(ms)) return 'Awaiting first fetch';
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60)   return `Updated ${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60)   return `Updated ${min}m ago`;
  const hr = Math.floor(min / 60);
  return `Updated ${hr}h ago`;
}

function Row({ color, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-slate-400">{label}:</span>
      </div>
      <span className="text-slate-100">{value}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ────────────────────────────────────────────────────────────────────────────
function monthKey(d) { return `${d.getFullYear()}-${d.getMonth()}`; }

function mapByMonth(series) {
  const m = new Map();
  for (const p of series || []) {
    if (p?.date && Number.isFinite(p.value)) m.set(monthKey(p.date), p.value);
  }
  return m;
}

function uniqueMonthsSince(...args) {
  const cutoff = args[args.length - 1];
  const seriesArgs = args.slice(0, -1);
  const set = new Map(); // key → Date
  for (const s of seriesArgs) {
    for (const p of s || []) {
      if (!p?.date) continue;
      if (cutoff && isAfter(cutoff, p.date)) continue;
      set.set(monthKey(p.date), new Date(p.date.getFullYear(), p.date.getMonth(), 1));
    }
  }
  return Array.from(set.values()).sort((a, b) => a - b);
}
